import { FormEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  createProgram,
  getProgramAdmin,
  updateProgram,
  uploadProgramImage,
  addProgramGalleryImage,
  deleteProgramGalleryImage,
  ProgramInput,
} from "../../../lib/adminApi";
import { useToast } from "../../../components/Toast";

const EMPTY: ProgramInput = {
  name: "",
  description: "",
  ageGroupFrom: null,
  ageGroupTo: null,
  durationMinutes: 45,
  maxChildrenPerSlot: 30,
  setupMinutes: 30,
  teardownMinutes: 30,
  pricePerChildCzk: 80,
  minPriceCzk: 2000,
  coverImageUrl: null,
  active: true,
};

export default function ProgramForm() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const toast = useToast();
  const nav = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const [state, setState] = useState<ProgramInput>(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [gallery, setGallery] = useState<{ id: string; url: string; alt: string | null }[]>([]);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const galleryRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isNew) return;
    getProgramAdmin(id!)
      .then((p) => {
        setState({
          name: p.name,
          description: p.description,
          ageGroupFrom: p.ageGroupFrom,
          ageGroupTo: p.ageGroupTo,
          durationMinutes: p.durationMinutes,
          maxChildrenPerSlot: p.maxChildrenPerSlot,
          setupMinutes: p.setupMinutes,
          teardownMinutes: p.teardownMinutes,
          pricePerChildCzk: p.pricePerChildCzk,
          minPriceCzk: p.minPriceCzk,
          coverImageUrl: p.coverImageUrl,
          active: p.active,
        });
        setGallery((p.images ?? []).map((i) => ({ id: i.id, url: i.url, alt: i.alt })));
      })
      .catch((e) => toast.show("error", e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function update<K extends keyof ProgramInput>(k: K, v: ProgramInput[K]) {
    setState((s) => ({ ...s, [k]: v }));
  }

  async function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadProgramImage(file);
      update("coverImageUrl", url);
      toast.show("success", "Cover obrázek nahrán.");
    } catch (err: any) {
      toast.show("error", err.message ?? "Upload selhal.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function onGalleryChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    setGalleryUploading(true);
    try {
      const img = await addProgramGalleryImage(id, file);
      setGallery((xs) => [...xs, { id: img.id, url: img.url, alt: img.alt }]);
      toast.show("success", "Obrázek přidán do galerie.");
    } catch (err: any) {
      toast.show("error", err.message ?? "Upload selhal.");
    } finally {
      setGalleryUploading(false);
      if (galleryRef.current) galleryRef.current.value = "";
    }
  }

  async function onGalleryDelete(imageId: string) {
    if (!id) return;
    if (!confirm("Smazat obrázek z galerie?")) return;
    try {
      await deleteProgramGalleryImage(id, imageId);
      setGallery((xs) => xs.filter((x) => x.id !== imageId));
      toast.show("success", "Smazáno.");
    } catch (err: any) {
      toast.show("error", err.message);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      // Vyčistíme prázdné stringy → null
      const payload: ProgramInput = {
        ...state,
        description: (state.description ?? "").trim() || null,
        coverImageUrl: state.coverImageUrl || null,
      };
      const saved = isNew
        ? await createProgram(payload)
        : await updateProgram(id!, payload);
      toast.show("success", isNew ? "Program vytvořen." : "Program uložen.");
      if (isNew) nav(`/admin/programs/${saved.id}`, { replace: true });
    } catch (err: any) {
      toast.show("error", err.message ?? "Uložení selhalo.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-slate-500">Načítám…</div>;

  // Náhled formule
  const exampleKids = 30;
  const examplePrice = Math.max(exampleKids * state.pricePerChildCzk, state.minPriceCzk);

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/admin/programs" className="text-sm text-slate-500 hover:text-slate-700">
            ← Zpět na programy
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-slate-900">
            {isNew ? "Nový program" : state.name || "Program"}
          </h1>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Levý sloupec — základní info */}
        <div className="space-y-6 lg:col-span-2">
          <Section title="Základní údaje">
            <Field label="Název" required>
              <input
                className="input"
                value={state.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Např. Cesta za hvězdami"
                required
              />
            </Field>
            <Field label="Popis" hint="Co děti zažijí, co se naučí…">
              <textarea
                className="input min-h-24"
                value={state.description ?? ""}
                onChange={(e) => update("description", e.target.value)}
              />
            </Field>
            <Grid2>
              <Field label="Věková skupina OD" hint="v letech">
                <input
                  type="number"
                  min={0}
                  className="input"
                  value={state.ageGroupFrom ?? ""}
                  onChange={(e) =>
                    update("ageGroupFrom", e.target.value === "" ? null : Number(e.target.value))
                  }
                />
              </Field>
              <Field label="Věková skupina DO">
                <input
                  type="number"
                  min={0}
                  className="input"
                  value={state.ageGroupTo ?? ""}
                  onChange={(e) =>
                    update("ageGroupTo", e.target.value === "" ? null : Number(e.target.value))
                  }
                />
              </Field>
            </Grid2>
          </Section>

          <Section
            title="Kapacita a trvání"
            hint="Pokud klient přihlásí víc dětí, než je kapacita na slot, program se odehraje víckrát za sebou."
          >
            <Grid2>
              <Field label="Trvání programu (min)" required>
                <input
                  type="number"
                  min={1}
                  className="input"
                  value={state.durationMinutes}
                  onChange={(e) => update("durationMinutes", Number(e.target.value) || 1)}
                  required
                />
              </Field>
              <Field label="Max dětí na jedno provedení" required>
                <input
                  type="number"
                  min={1}
                  className="input"
                  value={state.maxChildrenPerSlot}
                  onChange={(e) => update("maxChildrenPerSlot", Number(e.target.value) || 1)}
                  required
                />
              </Field>
              <Field label="Čas montáže (min)" hint="přičte se před programem, jen 1×">
                <input
                  type="number"
                  min={0}
                  className="input"
                  value={state.setupMinutes}
                  onChange={(e) => update("setupMinutes", Number(e.target.value) || 0)}
                />
              </Field>
              <Field label="Čas demontáže (min)" hint="přičte se po programu, jen 1×">
                <input
                  type="number"
                  min={0}
                  className="input"
                  value={state.teardownMinutes}
                  onChange={(e) => update("teardownMinutes", Number(e.target.value) || 0)}
                />
              </Field>
            </Grid2>
          </Section>

          <Section title="Ceny">
            <Grid2>
              <Field label="Cena za dítě (Kč)" required>
                <input
                  type="number"
                  min={0}
                  className="input"
                  value={state.pricePerChildCzk}
                  onChange={(e) => update("pricePerChildCzk", Number(e.target.value) || 0)}
                  required
                />
              </Field>
              <Field label="Minimální cena (Kč)" hint="použije se, když je děti málo">
                <input
                  type="number"
                  min={0}
                  className="input"
                  value={state.minPriceCzk}
                  onChange={(e) => update("minPriceCzk", Number(e.target.value) || 0)}
                  required
                />
              </Field>
            </Grid2>
            <div className="mt-4 rounded-lg bg-slate-50 p-3 text-sm">
              <div className="font-medium text-slate-700">Náhled výpočtu:</div>
              <div className="mt-1 text-slate-600">
                Při {exampleKids} dětech = max({exampleKids} × {state.pricePerChildCzk},{" "}
                {state.minPriceCzk}) = <strong>{examplePrice.toLocaleString("cs-CZ")} Kč</strong>
              </div>
            </div>
          </Section>
        </div>

        {/* Pravý sloupec — stav + obrázek */}
        <div className="space-y-6">
          <Section title="Stav">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={state.active ?? true}
                onChange={(e) => update("active", e.target.checked)}
                className="h-4 w-4"
              />
              Aktivní (zobrazit v rezervačním formuláři)
            </label>
          </Section>

          <Section title="Cover obrázek">
            {state.coverImageUrl ? (
              <div>
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  <img src={state.coverImageUrl} alt="Náhled" className="w-full" />
                </div>
                <button
                  type="button"
                  onClick={() => update("coverImageUrl", null)}
                  className="mt-2 text-sm text-red-600 hover:text-red-800"
                >
                  Odebrat obrázek
                </button>
              </div>
            ) : (
              <div className="rounded-lg border-2 border-dashed border-slate-300 p-6 text-center">
                <div className="text-3xl">📷</div>
                <div className="mt-2 text-sm text-slate-500">JPG, PNG, WebP (max 5 MB)</div>
              </div>
            )}

            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onFileChange}
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-secondary mt-3 w-full"
            >
              {uploading ? "Nahrávám…" : state.coverImageUrl ? "Změnit obrázek" : "Nahrát obrázek"}
            </button>
          </Section>

          {!isNew && (
            <Section title="Galerie programu">
              {gallery.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
                  Zatím žádné fotky. Nahrajte několik, aby se ukázaly na detailu programu.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {gallery.map((img) => (
                    <div key={img.id} className="group relative overflow-hidden rounded-lg border border-slate-200">
                      <img src={img.url} alt={img.alt ?? ""} className="aspect-square w-full object-cover" />
                      <button
                        type="button"
                        onClick={() => onGalleryDelete(img.id)}
                        className="absolute right-1 top-1 rounded-full bg-red-600/80 p-1 text-white opacity-0 transition hover:bg-red-700 group-hover:opacity-100"
                        title="Smazat"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={onGalleryChange} />
              <button
                type="button"
                onClick={() => galleryRef.current?.click()}
                disabled={galleryUploading}
                className="btn-secondary mt-3 w-full text-sm"
              >
                {galleryUploading ? "Nahrávám…" : "+ Přidat do galerie"}
              </button>
            </Section>
          )}
        </div>
      </div>

      <div className="sticky bottom-0 -mx-6 border-t border-slate-200 bg-white px-6 py-4">
        <div className="flex justify-end gap-3">
          <Link to="/admin/programs" className="btn-secondary">
            Zrušit
          </Link>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Ukládám…" : isNew ? "Vytvořit program" : "Uložit změny"}
          </button>
        </div>
      </div>
    </form>
  );
}

// ─── UI helpery ────────────────────────────────────────────

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card space-y-4">
      <div>
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function Grid2({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 md:grid-cols-2">{children}</div>;
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
    </label>
  );
}
